import React, { useCallback, useEffect, useRef, useState } from 'react';
import checkmarkIcon from '../assets/icons/checkmark.svg';

const CommanderConfirmationModal = ({
  title,
  confirmationText,
  actionLabel,
  pending = false,
  onClose,
  onConfirm
}) => {
  const [checked, setChecked] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const canConfirm = checked && hasSignature && !pending;

  const prepareCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * scale));
    canvas.height = Math.max(1, Math.floor(rect.height * scale));

    const ctx = canvas.getContext('2d');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111111';
  }, []);

  useEffect(() => {
    prepareCanvas();
    window.addEventListener('resize', prepareCanvas);
    return () => window.removeEventListener('resize', prepareCanvas);
  }, [prepareCanvas]);

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const beginSignature = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const point = getCanvasPoint(event);

    event.preventDefault();
    canvas.setPointerCapture?.(event.pointerId);
    drawingRef.current = true;
    lastPointRef.current = point;
    setHasSignature(true);

    ctx.beginPath();
    ctx.arc(point.x, point.y, 0.8, 0, Math.PI * 2);
    ctx.fillStyle = '#111111';
    ctx.fill();
  };

  const drawSignature = (event) => {
    if (!drawingRef.current || !lastPointRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const nextPoint = getCanvasPoint(event);

    event.preventDefault();
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(nextPoint.x, nextPoint.y);
    ctx.stroke();
    lastPointRef.current = nextPoint;
  };

  const endSignature = (event) => {
    drawingRef.current = false;
    lastPointRef.current = null;
    canvasRef.current?.releasePointerCapture?.(event.pointerId);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  return (
    <div className="commander-modal-overlay" role="presentation" onMouseDown={onClose}>
      <div className="commander-modal" role="dialog" aria-modal="true" aria-labelledby="commander-modal-title" onMouseDown={e => e.stopPropagation()}>
        <div className="commander-modal-header">
          <h2 id="commander-modal-title">{title}</h2>
          <button type="button" className="commander-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="commander-modal-body">
          <button
            type="button"
            className={`commander-modal-check ${checked ? 'checked' : ''}`}
            onClick={() => setChecked(value => !value)}
            aria-pressed={checked}
          >
            <span className="commander-modal-check-dot" aria-hidden="true" />
            <span>{confirmationText}</span>
          </button>

          <div className="commander-modal-sign-head">
            <p className="commander-modal-sign-label">You are about to {actionLabel.toLowerCase()}. Please sign below:</p>
            <button type="button" className="commander-modal-clear" onClick={clearSignature} disabled={!hasSignature || pending}>Clear</button>
          </div>
          <canvas
            ref={canvasRef}
            className="commander-modal-signature"
            aria-label="Commander signature drawing area"
            onPointerDown={beginSignature}
            onPointerMove={drawSignature}
            onPointerUp={endSignature}
            onPointerCancel={endSignature}
            onPointerLeave={endSignature}
          />
        </div>

        <div className="commander-modal-footer">
          <button
            type="button"
            className="commander-modal-action"
            disabled={!canConfirm}
            onClick={() => onConfirm(canvasRef.current?.toDataURL('image/png') || null)}
          >
            <span className="commander-modal-action-icon" aria-hidden="true">
              <img src={checkmarkIcon} alt="" className="app-icon app-icon-checkmark" />
            </span>
            <span>{pending ? 'Saving...' : actionLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommanderConfirmationModal;
